// src/services/authService.ts
// Google / Apple ログインの共通サービス
// - Web: signInWithPopup
// - iOS ネイティブ (Capacitor): Sign in with Apple はネイティブプラグイン経由
//   (App Store Guideline 4.8 対応)
import {
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    signInWithCredential,
    UserCredential,
} from 'firebase/auth';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { auth } from '@/firebase';

/** Capacitor ネイティブ環境かどうか */
export const isNativePlatform = (): boolean =>
    typeof window !== 'undefined' &&
    (window.location.protocol === 'capacitor:' ||
        Boolean((window as any).Capacitor?.isNativePlatform?.()));

/** iOS ネイティブかどうか（Apple ログインボタンの表示判定に使用） */
export const isIOSNative = (): boolean =>
    isNativePlatform() &&
    ((window as any).Capacitor?.getPlatform?.() === 'ios');

/** ランダム nonce を生成 */
const generateNonce = (length = 32): string => {
    const charset =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = new Uint8Array(length);
    crypto.getRandomValues(values);
    return Array.from(values, (v) => charset[v % charset.length]).join('');
};

/** SHA-256 ハッシュ（hex文字列） */
const sha256Hex = async (input: string): Promise<string> => {
    const data = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
};

/** Google ログイン */
export const signInWithGoogle = async (): Promise<UserCredential> => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
};

/**
 * Apple ログイン
 * - iOS ネイティブ: ASAuthorization 経由で identityToken を取得し
 *   Firebase の OAuthProvider('apple.com') 資格情報に変換
 * - Web: signInWithPopup
 */
export const signInWithApple = async (): Promise<UserCredential> => {
    if (isNativePlatform()) {
        const rawNonce = generateNonce();
        const hashedNonce = await sha256Hex(rawNonce);

        // iOS ネイティブ: ASAuthorization を使用。
        // clientId / redirectURI はネイティブ iOS では不要(Web/Android 用)であり、
        // 指定すると Web 認証フローに切り替わって失敗する端末があるため渡さない。
        const result = await SignInWithApple.authorize({
            scopes: 'email name',
            nonce: hashedNonce,
        } as any);

        const idToken = result.response?.identityToken;
        if (!idToken) {
            throw new Error('Appleサインインでトークンを取得できませんでした。時間をおいて再度お試しください。');
        }

        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
            idToken,
            rawNonce,
        });
        return signInWithCredential(auth, credential);
    }

    // Web フォールバック
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return signInWithPopup(auth, provider);
};
